import { Component, EventEmitter, inject, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { AngularSvgIconModule } from "angular-svg-icon";
import { ApeButtonComponent } from "../ape-button/ape-button.component";
import { UserDataService } from "../../services/data/user-data.service";
import { TranslateService } from "../../util/translate.service";
import { TRANSLATION_KEYS } from "../../constants/translations/translation-keys";
import { TranslatePipe } from "../../pipe/translation.filter.pipe";
import { BaseComponent } from "../../../ancestors/base/base.component";

/**
 * Componente per la barra di impersonificazione
 * Mostra un bar sticky quando l'utente corrente è impersonificato
 *
 * Caratteristiche:
 * - Posizionamento sticky in cima alla pagina
 * - Mostra il nome dell'utente impersonificato
 * - Pulsante per terminare l'impersonificazione
 * - Supporto completo per accessibilità
 */
@Component({
  selector: "ape-impersonation-bar",
  standalone: true,
  imports: [
    CommonModule,
    AngularSvgIconModule,
    TranslatePipe,
    ApeButtonComponent,
    TranslatePipe
  ],
  templateUrl: "./ape-impersonation-bar.component.html"
})
export class ApeImpersonationBarComponent extends BaseComponent {
  @Output() endImpersonation = new EventEmitter<void>();

  protected readonly TRANSLATION_KEYS = TRANSLATION_KEYS;

  private userDataService = inject(UserDataService);

  private translate = inject(TranslateService);

  /**
   * Verifica se l'utente corrente è impersonificato
   * @returns true se l'utente è impersonificato, false altrimenti
   */
  protected get isImpersonated(): boolean {
    const payload = this.userDataService.getJwtPayload();
    return !!payload?.impersonifiedByUserId;
  }

  /**
   * Nome completo dell'utente impersonificato
   * @returns Nome e cognome dell'utente impersonificato
   */
  protected get impersonatedUserName(): string {
    const payload = this.userDataService.getJwtPayload();
    const user = payload?.user;
    if (!user) return "";

    const forename = user.forename || "";
    const surname = user.surname || "";
    return `${ forename } ${ surname }`.trim() || user.email || "";
  }

  /**
    * Emite l'evento per terminare l'impersonificazione
    */
  protected onEndImpersonation(): void {
    this.endImpersonation.emit();
  }
}
